import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useRef } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { toast } from "sonner";

import { useDefaultValues } from "Forms/Hooks/useDefaultValues";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import type {
  CountryCodes,
  LanguageCodes,
  ProjectDetailDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useUpdateProjectMutation } from "Projects/Api/Mutations/useUpdateProjectMutation";
import {
  type CreateProjectFormData,
  createProjectFormSchema,
} from "Projects/Forms/createProjectFormSchema";
import { ProjectForm } from "Projects/Forms/ProjectForm";
import { BlModal } from "Shared/Dialogs/BlModal";
import { apiStringToDate, dateToApiString } from "Shared/Utils/Format/date";

interface EditProjectModalProps {
  isOpen: boolean;
  project: ProjectDetailDto;
  onClose: () => void;
}

export const EditProjectModal = ({
  isOpen,
  project,
  onClose,
}: EditProjectModalProps): React.ReactNode => {
  const { t } = useResource();
  const updateProjectMutation = useUpdateProjectMutation(project.projectID);

  const formRef = useRef<UseFormReturn<CreateProjectFormData> | null>(null);

  const defaultValues = useDefaultValues(
    () => ({
      name: project.name,
      clientID: project.clientID ?? "",
      moduleID: project.moduleID,
      industry: project.industry,
      language: project.language ?? "",
      licensedCountries: project.licensedCountries,
      jobsLimit: project.jobsLimit,
      startDate: apiStringToDate(project.startDate) ?? undefined,
      endDate: apiStringToDate(project.endDate) ?? undefined,
      hasExclusiveContracts: false,
      exclusiveContracts: [],
    }),
    [project],
    formRef,
  );

  const readOnlyExclusiveContracts = useMemo(
    () =>
      (project.actors ?? [])
        .filter((a) => a.isExclusiveContract)
        .map((a) => ({
          actorName: `${a.firstName} ${a.lastName}`.trim(),
          price: a.exclusiveContractPrice ?? 0,
          jobLimit: a.exclusiveContractJobLimit ?? 0,
        })),
    [project.actors],
  );

  const form = useForm<CreateProjectFormData>({
    resolver: zodResolver(createProjectFormSchema),
    defaultValues,
  });

  formRef.current = form;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = form;

  useHandleServerError({
    form,
    error: updateProjectMutation.error,
    formKey: "name",
    resource: Resources.ApiErrors.SaveProject,
  });

  const onSubmit = async (data: CreateProjectFormData) => {
    try {
      await updateProjectMutation.mutateAsync({
        name: data.name,
        clientID: data.clientID,
        moduleID: data.moduleID,
        industry: data.industry,
        language: data.language as LanguageCodes,
        licensedCountries: data.licensedCountries as CountryCodes[],
        jobsLimit: data.jobsLimit,
        startDate: dateToApiString(data.startDate),
        endDate: dateToApiString(data.endDate),
        exclusiveContracts: [],
      });
      toast.success(t(Resources.Projects.EditProjectModal.SuccessMessage));
      onClose();
    } catch {
      // field errors shown in form, non-field errors as toast via useHandleServerError
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Projects.EditProjectModal.Title)}
      onCancel={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Projects.EditProjectModal.Edit)}
      maxWidth="800px"
    >
      <ProjectForm
        control={control}
        errors={errors}
        disableModule
        moduleOptionsOverride={[
          { value: project.moduleID, label: project.moduleName },
        ]}
        readOnlyExclusiveContracts={readOnlyExclusiveContracts}
      />
    </BlModal>
  );
};
